import { useEffect, useMemo, useState } from "react";
import { PrinterIcon } from "@heroicons/react/24/outline";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput } from "@/components/ui/Field";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { Spinner } from "@/components/ui/Spinner";
import {
  ARTIKEL_ETIKETT_COPIES_MAX,
  ARTIKEL_ETIKETT_LENGTH_DEFAULT,
  ARTIKEL_ETIKETT_LENGTH_MAX,
  ARTIKEL_ETIKETT_LENGTH_MIN,
  ARTIKEL_ETIKETT_MAX,
  clampEtikettCopies,
  clampLabelLengthMm,
  currentArtikelEtikettLocation,
  loadStoredLengthMm,
  loadStoredTapeMm,
  storeLengthMm,
  storeTapeMm,
  type ArtikelEtikettItem,
  type TapeMm,
  TAPE_OPTIONS,
} from "@/lib/artikelEtikett";
import {
  downloadEtiketten,
  printEtikettenViaSystemDialog,
} from "@/lib/artikelEtikettPrint";
import { renderArtikelEtikett, type RenderedArtikelEtikett } from "@/lib/artikelEtikettRender";
import { isWebUsbAvailable, printLabelsOnPtP910bt } from "@/lib/ptP910bt";
import { toast } from "@/lib/toast";

function expandLabelCopies(
  labels: RenderedArtikelEtikett[],
  copiesById: Record<number, number>,
): RenderedArtikelEtikett[] {
  const out: RenderedArtikelEtikett[] = [];
  for (const label of labels) {
    const count = clampEtikettCopies(copiesById[label.item.id] ?? 1);
    for (let i = 0; i < count; i++) out.push(label);
  }
  return out;
}

function syncOverrideMap(
  items: ArtikelEtikettItem[],
  prev: Record<number, number>,
  fallback: number,
): Record<number, number> {
  const next: Record<number, number> = {};
  let unchanged = Object.keys(prev).length === items.length;
  for (const item of items) {
    const value = prev[item.id] ?? fallback;
    next[item.id] = value;
    if (prev[item.id] !== value) unchanged = false;
  }
  return unchanged ? prev : next;
}

function applyToAll(items: ArtikelEtikettItem[], value: number): Record<number, number> {
  const next: Record<number, number> = {};
  for (const item of items) next[item.id] = value;
  return next;
}

const TAPE_PREVIEW_PX_PER_MM = 3.4;

function EtikettTapePreview({
  label,
  tapeMm,
  lengthInput,
  copiesInput,
  onLengthChange,
  onLengthBlur,
  onCopiesChange,
  onCopiesBlur,
  onRemove,
}: {
  label: RenderedArtikelEtikett;
  tapeMm: TapeMm;
  lengthInput: string;
  copiesInput: string;
  onLengthChange: (value: string) => void;
  onLengthBlur: () => void;
  onCopiesChange: (value: string) => void;
  onCopiesBlur: () => void;
  onRemove?: (id: number) => void;
}) {
  const pxPerMm = TAPE_PREVIEW_PX_PER_MM;
  const tapeH = tapeMm * pxPerMm;
  const tapeW = label.widthMm * pxPerMm;
  const printH = label.heightMm * pxPerMm;
  return (
    <figure className="rounded-ios border border-separator bg-fill-secondary/40 p-3">
      <div className="overflow-x-auto">
        <div
          className="relative mx-auto rounded-[3px] border border-black/15 bg-[#f3f1ea] shadow-sm"
          style={{ width: tapeW, height: tapeH }}
        >
          <img
            src={label.dataUrl}
            alt={`Etikett ${label.item.heroNr}`}
            className="absolute left-0 max-w-none"
            style={{
              width: tapeW,
              height: printH,
              top: (tapeH - printH) / 2,
            }}
          />
        </div>
      </div>
      <figcaption className="mt-2 space-y-2">
        <div className="flex items-center justify-between gap-2 text-xs text-label-secondary">
          <span className="truncate">
            {Math.round(label.widthMm)} × {tapeMm} mm
            {label.item.heroNr ? ` · ${label.item.heroNr}` : ""}
            {label.item.name ? ` · ${label.item.name}` : ""}
          </span>
          {onRemove ? (
            <button
              type="button"
              className="shrink-0 text-accent hover:underline"
              onClick={() => onRemove(label.item.id)}
            >
              Entfernen
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:max-w-sm">
          <TextInput
            label="Länge (mm)"
            type="number"
            min={ARTIKEL_ETIKETT_LENGTH_MIN}
            max={ARTIKEL_ETIKETT_LENGTH_MAX}
            step={1}
            value={lengthInput}
            onChange={(e) => onLengthChange(e.target.value)}
            onBlur={onLengthBlur}
          />
          <TextInput
            label="Anzahl"
            type="number"
            min={1}
            max={ARTIKEL_ETIKETT_COPIES_MAX}
            value={copiesInput}
            onChange={(e) => onCopiesChange(e.target.value)}
            onBlur={onCopiesBlur}
          />
        </div>
      </figcaption>
    </figure>
  );
}

export function ArtikelEtikettDialog({
  open,
  onClose,
  items,
  loading = false,
  truncated = false,
  total = 0,
  error = null,
  onRemoveItem,
}: {
  open: boolean;
  onClose: () => void;
  items: ArtikelEtikettItem[];
  loading?: boolean;
  truncated?: boolean;
  total?: number;
  error?: string | null;
  onRemoveItem?: (id: number) => void;
}) {
  const [tapeMm, setTapeMm] = useState<TapeMm>(24);
  const [lengthMm, setLengthMm] = useState(ARTIKEL_ETIKETT_LENGTH_DEFAULT);
  const [lengthInput, setLengthInput] = useState(String(ARTIKEL_ETIKETT_LENGTH_DEFAULT));
  const [copies, setCopies] = useState("1");
  const [lengthById, setLengthById] = useState<Record<number, number>>({});
  const [lengthInputById, setLengthInputById] = useState<Record<number, string>>({});
  const [copiesById, setCopiesById] = useState<Record<number, number>>({});
  const [copiesInputById, setCopiesInputById] = useState<Record<number, string>>({});
  const [labels, setLabels] = useState<RenderedArtikelEtikett[]>([]);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [printing, setPrinting] = useState<"usb" | "system" | null>(null);
  const webUsb = isWebUsbAvailable();

  useEffect(() => {
    if (!open) {
      setLengthById({});
      setLengthInputById({});
      setCopiesById({});
      setCopiesInputById({});
      setCopies("1");
      return;
    }
    const nextTape = loadStoredTapeMm();
    const nextLength = loadStoredLengthMm();
    setTapeMm(nextTape);
    setLengthMm(nextLength);
    setLengthInput(String(nextLength));
    setCopies("1");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const storedLength = loadStoredLengthMm();
    setLengthById((prev) =>
      Object.keys(prev).length === 0
        ? applyToAll(items, storedLength)
        : syncOverrideMap(items, prev, storedLength),
    );
    setLengthInputById((prev) => {
      if (Object.keys(prev).length === 0) {
        return Object.fromEntries(items.map((item) => [item.id, String(storedLength)]));
      }
      const next: Record<number, string> = {};
      for (const item of items) next[item.id] = prev[item.id] ?? String(storedLength);
      return next;
    });
    setCopiesById((prev) =>
      Object.keys(prev).length === 0 ? applyToAll(items, 1) : syncOverrideMap(items, prev, 1),
    );
    setCopiesInputById((prev) => {
      if (Object.keys(prev).length === 0) {
        return Object.fromEntries(items.map((item) => [item.id, "1"]));
      }
      const next: Record<number, string> = {};
      for (const item of items) next[item.id] = prev[item.id] ?? "1";
      return next;
    });
  }, [open, items]);

  useEffect(() => {
    if (!open) {
      setLabels([]);
      setRenderError(null);
      setRendering(false);
      return;
    }
    if (loading || items.length === 0) {
      setLabels([]);
      return;
    }
    let cancelled = false;
    setRendering(true);
    setRenderError(null);
    const loc = currentArtikelEtikettLocation();
    try {
      const next = items.map((item) =>
        renderArtikelEtikett(item, tapeMm, loc, lengthById[item.id] ?? lengthMm),
      );
      if (!cancelled) setLabels(next);
    } catch (err) {
      if (!cancelled) {
        setLabels([]);
        setRenderError(err instanceof Error ? err.message : "Etiketten konnten nicht erzeugt werden.");
      }
    } finally {
      if (!cancelled) setRendering(false);
    }
    return () => {
      cancelled = true;
    };
  }, [open, items, tapeMm, lengthMm, lengthById, loading]);

  const printCount = useMemo(
    () => items.reduce((sum, item) => sum + clampEtikettCopies(copiesById[item.id] ?? 1), 0),
    [items, copiesById],
  );
  const mixedLengths = useMemo(() => {
    const values = new Set(items.map((item) => lengthById[item.id] ?? lengthMm));
    return values.size > 1;
  }, [items, lengthById, lengthMm]);

  function handleTapeChange(value: string) {
    const next = Number(value);
    if (next !== 12 && next !== 18 && next !== 24 && next !== 36) return;
    setTapeMm(next);
    storeTapeMm(next);
  }

  function applyLengthToAll(next: number) {
    setLengthMm(next);
    setLengthInput(String(next));
    storeLengthMm(next);
    setLengthById(applyToAll(items, next));
    setLengthInputById(Object.fromEntries(items.map((item) => [item.id, String(next)])));
  }

  function commitLength(raw: string) {
    applyLengthToAll(clampLabelLengthMm(Number(raw.replace(",", ".").trim())));
  }

  function handleLengthChange(value: string) {
    setLengthInput(value);
    const parsed = Number(value.replace(",", ".").trim());
    if (!Number.isFinite(parsed)) return;
    if (parsed < ARTIKEL_ETIKETT_LENGTH_MIN || parsed > ARTIKEL_ETIKETT_LENGTH_MAX) return;
    applyLengthToAll(Math.round(parsed));
  }

  function applyCopiesToAll(next: number) {
    const copiesValue = clampEtikettCopies(next);
    setCopies(String(copiesValue));
    setCopiesById(applyToAll(items, copiesValue));
    setCopiesInputById(Object.fromEntries(items.map((item) => [item.id, String(copiesValue)])));
  }

  function handleCopiesChange(value: string) {
    setCopies(value);
    const parsed = Number(value.replace(",", ".").trim());
    if (!value.trim() || !Number.isFinite(parsed)) return;
    applyCopiesToAll(parsed);
  }

  function handleItemLengthChange(id: number, value: string) {
    setLengthInputById((prev) => ({ ...prev, [id]: value }));
    const parsed = Number(value.replace(",", ".").trim());
    if (!Number.isFinite(parsed)) return;
    if (parsed < ARTIKEL_ETIKETT_LENGTH_MIN || parsed > ARTIKEL_ETIKETT_LENGTH_MAX) return;
    setLengthById((prev) => ({ ...prev, [id]: Math.round(parsed) }));
  }

  function handleItemLengthBlur(id: number) {
    const next = clampLabelLengthMm(Number((lengthInputById[id] ?? "").replace(",", ".").trim()));
    setLengthById((prev) => ({ ...prev, [id]: next }));
    setLengthInputById((prev) => ({ ...prev, [id]: String(next) }));
  }

  function handleItemCopiesChange(id: number, value: string) {
    setCopiesInputById((prev) => ({ ...prev, [id]: value }));
    const parsed = Number(value.replace(",", ".").trim());
    if (!Number.isFinite(parsed)) return;
    setCopiesById((prev) => ({ ...prev, [id]: clampEtikettCopies(parsed) }));
  }

  function handleItemCopiesBlur(id: number) {
    const next = clampEtikettCopies(Number((copiesInputById[id] ?? "").replace(",", ".").trim()));
    setCopiesById((prev) => ({ ...prev, [id]: next }));
    setCopiesInputById((prev) => ({ ...prev, [id]: String(next) }));
  }

  async function handleUsbPrint() {
    if (!labels.length) return;
    setPrinting("usb");
    try {
      const result = await printLabelsOnPtP910bt(expandLabelCopies(labels, copiesById), tapeMm);
      if (result.usedTapeMm !== tapeMm) {
        toast.info(`Gedruckt auf eingelegtem ${result.usedTapeMm}-mm-Band.`);
      } else {
        toast.success(
          printCount === 1 ? "Etikett wird gedruckt." : `${printCount} Etiketten werden gedruckt.`,
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Direktdruck ist fehlgeschlagen.";
      if (err instanceof DOMException && err.name === "NotFoundError") {
        toast.error("Kein PT-P910BT ausgewählt. Drucker per USB verbinden und erneut versuchen.");
      } else {
        toast.error(message);
      }
    } finally {
      setPrinting(null);
    }
  }

  async function handleSystemPrint() {
    if (!labels.length) return;
    setPrinting("system");
    try {
      const mode = await printEtikettenViaSystemDialog(expandLabelCopies(labels, copiesById), tapeMm);
      if (mode === "raw") {
        const firstId = labels[0]?.item.id;
        const firstLength = firstId != null ? (lengthById[firstId] ?? lengthMm) : lengthMm;
        toast.success(
          printCount === 1
            ? `Etikett geht an den PT-P910BT (${firstLength} × ${tapeMm} mm).`
            : `${printCount} Etiketten gehen an den PT-P910BT.`,
        );
      } else {
        toast.success(
          `Druckdialog geöffnet. PT-P910BT und ${tapeMm} mm Band wählen, Maßstab 100 %.`,
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Systemdruck ist fehlgeschlagen.");
    } finally {
      setPrinting(null);
    }
  }

  const busy = printing != null || rendering || loading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Artikel-Etiketten"
      description="QR-Code, Artikelnummer und Name für den PT-P910BT"
      size="xl"
      bodyClassName="overflow-y-auto max-h-[75vh]"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => downloadEtiketten(labels)} disabled={!labels.length || busy}>
            PNG speichern
          </Button>
          <Button type="button" variant="secondary" onClick={() => void handleSystemPrint()} loading={printing === "system"} disabled={!labels.length || busy}>
            Über Systemdrucker
          </Button>
          <Button
            type="button"
            onClick={() => void handleUsbPrint()}
            loading={printing === "usb"}
            disabled={!labels.length || busy || !webUsb}
            leftIcon={<PrinterIcon className="h-4 w-4" />}
          >
            Auf PT-P910BT
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-label-secondary">
          Der PT-P910BT hat kein WLAN. In der Desktop-App sendet „Über Systemdrucker“ Bandbreite,
          Länge und Schnitt direkt an den Windows-Drucker – ohne den Brother-Einstellungsdialog.
          Im Browser kann Windows diese Werte nicht vorausfüllen; dort USB nutzen oder im
          Brother-Dialog {tapeMm} mm und {lengthMm} mm setzen.
        </p>

        {error ? <Alert variant="error">{error}</Alert> : null}
        {renderError ? <Alert variant="error">{renderError}</Alert> : null}
        {truncated ? (
          <Alert variant="warning">
            Es werden die ersten {ARTIKEL_ETIKETT_MAX} von {total.toLocaleString("de-DE")} Artikeln
            vorbereitet.
          </Alert>
        ) : null}
        {!webUsb ? (
          <Alert>
            Direktdruck per USB ist in diesem Browser nicht verfügbar. Bitte Chrome oder Edge
            verwenden oder über den Systemdrucker drucken.
          </Alert>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <NativeSelectField
            label="Bandbreite"
            value={String(tapeMm)}
            onChange={(e) => handleTapeChange(e.target.value)}
          >
            {TAPE_OPTIONS.map((mm) => (
              <option key={mm} value={mm}>
                {mm} mm TZe-Band
              </option>
            ))}
          </NativeSelectField>
          <TextInput
            label="Länge für alle (mm)"
            type="number"
            min={ARTIKEL_ETIKETT_LENGTH_MIN}
            max={ARTIKEL_ETIKETT_LENGTH_MAX}
            step={1}
            value={lengthInput}
            onChange={(e) => handleLengthChange(e.target.value)}
            onBlur={() => commitLength(lengthInput)}
            hint={`${ARTIKEL_ETIKETT_LENGTH_MIN}–${ARTIKEL_ETIKETT_LENGTH_MAX} mm, gilt für alle`}
          />
          <TextInput
            label="Anzahl für alle"
            type="number"
            min={1}
            max={ARTIKEL_ETIKETT_COPIES_MAX}
            value={copies}
            onChange={(e) => handleCopiesChange(e.target.value)}
            onBlur={() => applyCopiesToAll(Number(copies.replace(",", ".").trim()))}
            hint={`1–${ARTIKEL_ETIKETT_COPIES_MAX}, gilt für alle`}
          />
        </div>

        {loading || rendering ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : labels.length === 0 ? (
          <p className="py-6 text-sm text-label-secondary">Keine Artikel ausgewählt.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-label-secondary">
              {items.length === 1
                ? "1 Etikett"
                : `${items.length.toLocaleString("de-DE")} Etiketten`}
              {mixedLengths
                ? ` · gemischte Längen × ${tapeMm} mm`
                : ` · ${lengthMm} × ${tapeMm} mm`}
              {` · ${printCount.toLocaleString("de-DE")} Stück gesamt`}
            </p>
            <div className="space-y-3">
              {labels.map((label) => (
                <EtikettTapePreview
                  key={label.item.id}
                  label={label}
                  tapeMm={tapeMm}
                  lengthInput={lengthInputById[label.item.id] ?? String(lengthById[label.item.id] ?? lengthMm)}
                  copiesInput={copiesInputById[label.item.id] ?? String(copiesById[label.item.id] ?? 1)}
                  onLengthChange={(value) => handleItemLengthChange(label.item.id, value)}
                  onLengthBlur={() => handleItemLengthBlur(label.item.id)}
                  onCopiesChange={(value) => handleItemCopiesChange(label.item.id, value)}
                  onCopiesBlur={() => handleItemCopiesBlur(label.item.id)}
                  onRemove={onRemoveItem}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
